import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { EmptyState, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { rglConfig } from 'analytics/components/panels/utils/theming/theming';
import { groupInstruments } from '../../utils/groupInstruments';
import { ChartInstrument } from '../instruments/chartInstrument/chartInstrument';
import { HeatmapInstrument } from '../instruments/heatmapInstrument/heatmapInstrument';
import { MetricInstrument } from '../instruments/metricInstrument/metricInstrument';
import { PresenceInstrument } from '../instruments/presenceInstrument/presenceInstrument';
import { TableInstrument } from '../instruments/tableInstrument/tableInstrument';
import { MetricInstrumentsGrid } from './components/metricInstrumentsGrid/metricInstrumentsGrid';
import styles from './instrumentRenderer.module.scss';
export const InstrumentRenderer = memo(function InstrumentRenderer({ panel, instruments, isFetching = false, isError = false, onReadInstruments, onSetInstrumentSettings, }) {
    const handleChangeGranularity = useCallback((granularity) => {
        onSetInstrumentSettings === null || onSetInstrumentSettings === void 0 ? void 0 : onSetInstrumentSettings({ granularity });
    }, [onSetInstrumentSettings]);
    const groups = useMemo(() => groupInstruments(instruments), [instruments]);
    const metricInstruments = useMemo(() => {
        const { metrics } = groups;
        if (!metrics.length)
            return null;
        return (_jsx(MetricInstrumentsGrid, Object.assign({ fluid: panel.layout.w === rglConfig.cols }, { children: metrics.map((instrument, index) => (_jsx(MetricInstrument, { instrument: instrument }, `${panel.id}-${instrument.type}-${index}`))) })));
    }, [groups, panel.id, panel.layout.w]);
    const otherInstruments = useMemo(() => {
        const { chart, heatmap, table, presence } = groups;
        if (chart) {
            return (_jsx(ChartInstrument, { instrument: chart, panel: panel, onChangeGranularity: handleChangeGranularity }));
        }
        if (heatmap)
            return _jsx(HeatmapInstrument, { instrument: heatmap });
        if (table)
            return (_jsx(TableInstrument, { instrument: table, panel: panel, onReadInstruments: onReadInstruments }));
        if (presence)
            return _jsx(PresenceInstrument, { instrument: presence });
        return null;
    }, [groups, handleChangeGranularity, onReadInstruments, panel]);
    if (isError) {
        return (_jsx(FlexBox, Object.assign({ flex: "1", justifyContent: "center", alignItems: "center" }, { children: _jsx(EmptyState, { title: "Failed to load data", description: _jsxs(_Fragment, { children: [_jsx("span", { children: "Something went wrong while fetching the data." }), ' ', _jsx("a", Object.assign({ href: "#", onClick: () => onReadInstruments() }, { children: "Please try again." }))] }), icon: null }) })));
    }
    // The loader is displayed as an overlay when data already exists and is being refreshed.
    const loaderClass = classnames(styles.loader, {
        [styles.loaderOverlaid]: instruments.length && isFetching,
    });
    return (_jsxs(_Fragment, { children: [isFetching && (_jsx(FlexBox, Object.assign({ className: loaderClass, flex: "1", justifyContent: "center", alignItems: "center" }, { children: _jsx(Spinner, {}) }))), instruments.length > 0 && (_jsxs("div", Object.assign({ className: styles.root }, { children: [metricInstruments, otherInstruments] })))] }));
});
